/**
 * Single source of truth for product match-explanation API calls.
 * Logged-in users defer to the Shopper Model for feature/usage signals, but
 * an explicit budget from a live AI session/wizard is always forwarded so
 * the server can prefer it over the aggregated model (keeps budget copy
 * consistent across the whole product page). Guests always send their
 * resolved chat/wizard intent.
 */

import api from "./api/axiosInstance";
import { capMatchPercent } from "./matchScoreUtils";
import { hasMatchIntent, resolveMatchIntent } from "./resolveMatchIntent";

const CACHE_DURATION = 5 * 60 * 1000;
const matchExplanationCache = new Map();

export function getMatchExplanationCacheKey(productId, userId = null) {
  return `${productId}_${userId || "guest"}`;
}

/**
 * Request body for match-explanation and alternatives APIs.
 * Logged-in → only an explicit budget (if any) is forwarded, letting the
 * Shopper Model own everything else. Guest → resolved chat/wizard intent.
 */
export function getMatchExplanationBody(user, options = {}) {
  const { aiSession = null } = options;
  const resolved = resolveMatchIntent({ aiSession, user }).wizardAnswers;

  if (user?.id || user?._id) {
    const budget = resolved?.budget;
    const hasExplicitBudget =
      budget && (budget.min != null || budget.max != null);
    return hasExplicitBudget ? { budget } : {};
  }

  return hasMatchIntent(resolved) ? resolved : {};
}

export function readMatchExplanationCache(productId, userId = null) {
  const key = getMatchExplanationCacheKey(productId, userId);
  const cached = matchExplanationCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached;
  }
  return null;
}

export function writeMatchExplanationCache(productId, userId, payload) {
  const key = getMatchExplanationCacheKey(productId, userId);
  matchExplanationCache.set(key, {
    ...payload,
    timestamp: Date.now(),
  });
}

/**
 * Fetch harmonized match explanation for a product.
 * @returns {Promise<{ score: number|null, match: object|null, scoring: object|null, lowConfidence: boolean }|null>}
 */
export async function fetchProductMatchExplanation(productId, options = {}) {
  if (!productId) return null;

  const userId = options.userId || null;
  const body =
    options.body !== undefined
      ? options.body
      : getMatchExplanationBody(options.user);

  const cached = readMatchExplanationCache(productId, userId);
  if (cached && !options.skipCache) {
    return cached;
  }

  try {
    const res = await api.post(
      `/products/${productId}/match-explanation`,
      body,
    );

    if (res.data?.match) {
      const identityRequired =
        res.data?.scoring?.identityRequired === true ||
        res.data.match.overallScore === null;

      // When the server signals the user hasn't completed their shopping
      // profile, we have no valid score to display — return null so the
      // badge is hidden rather than showing "0% Match".
      if (identityRequired) {
        return {
          score: null,
          match: res.data.match,
          scoring: res.data.scoring || null,
          lowConfidence: true,
          identityRequired: true,
        };
      }

      if (res.data.match.overallScore !== undefined) {
        const score = capMatchPercent(res.data.match.overallScore);
        const lowConfidence =
          res.data?.scoring?.source === "low_confidence" ||
          (res.data?.scoring?.confidence?.dataPoints ?? 0) < 2;

        const result = {
          score,
          match: res.data.match,
          scoring: res.data.scoring || null,
          lowConfidence,
          identityRequired: false,
        };

        writeMatchExplanationCache(productId, userId, result);
        return result;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function invalidateProductMatchExplanationCache(
  productId,
  userId = null,
) {
  if (productId) {
    matchExplanationCache.delete(
      getMatchExplanationCacheKey(productId, userId),
    );
  }
}
